import React from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from '@headlessui/react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';

interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  type?: 'warning' | 'danger';
}

export default function ConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText,
  cancelText,
  type = 'warning'
}: ConfirmDialogProps) {
  const { t } = useTranslation(['common_ui', 'common']);
  const resolvedConfirmText = confirmText ?? t('common_ui:dialog.confirm.confirm');
  const resolvedCancelText = cancelText ?? t('common:cancel');

  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/40" aria-hidden="true" />

      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="mx-auto max-w-sm w-full bg-bg-surface rounded-xl border border-border-subtle shadow-xl">
          <div className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${type === 'danger' ? 'bg-error/10' : 'bg-warning/10'
                }`}>
                <ExclamationTriangleIcon className={`w-6 h-6 ${type === 'danger' ? 'text-error' : 'text-warning'
                  }`} />
              </div>
              <Dialog.Title className="text-lg font-semibold text-gray-50">
                {title}
              </Dialog.Title>
            </div>

            <p className="text-sm text-gray-400 mb-6">
              {message}
            </p>

            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-300 bg-bg-elevated hover:bg-bg-form border border-border-subtle rounded-lg transition-colors cursor-pointer"
              >
                {resolvedCancelText}
              </button>
              <button
                onClick={handleConfirm}
                className={`flex-1 px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors cursor-pointer ${type === 'danger'
                    ? 'bg-error hover:bg-red-600'
                    : 'bg-primary hover:bg-primary-dark'
                  }`}
              >
                {resolvedConfirmText}
              </button>
            </div>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
